/**
 * Propose-step primitives, extracted from the original `ProposeStep` body. Two independent section
 * blocks: the conversation-guide resource downloads and the recommended discovery-questions list. Each
 * is a self-contained section (its own `Block` wrapper) so a `step_layout` can order, drop, or repeat
 * them.
 *
 * Section title/lead come from the primitive's authored `config`, falling back to the resolved
 * `sectionHeadings` (the same source the original body read); content data comes from the shared
 * `RunnerContext` plus the existing DB resolvers. No presentation is duplicated: these reuse the same
 * `Block`/`BlurTextarea`/`ui` leaves the body used, and the same managed-asset-or-bundled download seam.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { IQ_SCENARIO_KEY, recommendedKey } from '../../config/content';
import { resolveContentBlocks } from '../../api';
import {
  resolveAssetForStep,
  resolveDeckSourceForStep,
  resolveFileLabelForStep,
  resolveIqScenarios,
  resolveQuestionsForStep,
  resolveRecommendedQuestions,
  resolveScenariosFor,
} from '../../api/resolveContent';
import { getCampaignAssetDownload } from '../../../../../support/campaignAssets';
import { deriveLdosFacts, reconcileIqScenario } from '../behaviors';
import { useWorkshopStore } from '../../store/workshopStore';
import { useResolveText } from '../RunnerTokenContext';
import { Block, cx, ui } from '../../components/ui/primitives';
import { asIconName } from '../../components/ui/Icon';
import { sectionHeadings } from '../../config/sectionHeadings';
import { BlurTextarea } from '../../components/ui/BlurTextarea';
import s from '../../components/steps/steps.module.css';
import type { PrimitiveProps } from './registry';

// The maintained email template and one-pager are managed campaign assets: the step declares a file
// ref for each slot (`email` / `onepager`) and the content team uploads/replaces the file in Admin,
// served to the runner via a short-lived signed GET. There is no bundled `public/` fallback — a slot
// with no uploaded asset simply shows no button, exactly like the pitch-deck slot, so nothing
// campaign-specific is shipped in the build.

const NO_QUESTIONS: string[] = [];

// Trigger a browser download of a URL. For the bundled same-origin path a download-attributed anchor
// is enough; a signed cross-origin S3 URL carries its own `Content-Disposition`, so the anchor's
// `download` hint is ignored but harmless.
function triggerDownload(href: string, filename: string) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * The conversation-guide downloads: the uploaded pitch deck (only when a real asset is present), plus
 * the email template and one-pager (managed asset when referenced, else the bundled `public/` file).
 * Title/lead come from `metadata.runner.guide*`, threaded through `config`.
 */
export function ConversationGuide({ config }: PrimitiveProps): JSX.Element {
  const [deckBusy, setDeckBusy] = useState(false);
  const [, setError] = useState<string | null>(null);

  const deckSource = resolveDeckSourceForStep('propose', 'pitch');
  // The email template and one-pager are managed campaign assets when the step references an uploaded
  // file for the `email` / `onepager` slot (undefined otherwise → the bundled static path is served).
  const emailAsset = resolveAssetForStep('propose', 'email');
  const onePagerAsset = resolveAssetForStep('propose', 'onepager');
  const onePagerSecurityAsset = resolveAssetForStep('propose', 'onepager-security');

  // Whether the deck slot points at an uploaded managed asset. The conversation guide is ONLY the
  // uploaded file the content team maintains — there is no generated/placeholder deck to hand over, so
  // the button appears only when a real asset is uploaded, and the label states that file's format.
  const deckIsManaged = deckSource.assetId != null && !!deckSource.objectKey;
  const deckFormat = (deckSource.objectKey?.split('.').pop() || 'file').toUpperCase();

  // The author-set, end-user-facing label for each slot's file ref (edited in Admin's Files panel).
  // Drives both the button text and the download filename; falls back to the shipped wording when
  // blank, so an un-authored step is byte-identical.
  const deckLabel = resolveFileLabelForStep('propose', 'deck');
  const emailLabel = resolveFileLabelForStep('propose', 'email');
  const onePagerLabel = resolveFileLabelForStep('propose', 'onepager');
  const onePagerSecurityLabel = resolveFileLabelForStep('propose', 'onepager-security');

  // The email template (.docx) and one-pager (.pdf) are served ONLY from their managed campaign asset
  // (a short-lived signed GET, filename-attributed). There is no bundled fallback: the button is
  // rendered only when a real asset is uploaded, and a download failure is surfaced rather than
  // handing over a placeholder file, which is not real customer content.
  async function downloadManagedAsset(
    asset: ReturnType<typeof resolveAssetForStep>,
    filename: string,
  ) {
    if (asset?.id == null) return;
    setError(null);
    try {
      const { url } = await getCampaignAssetDownload(asset.id, filename);
      if (url) {
        triggerDownload(url, filename);
      } else {
        setError("Couldn't download that file from storage. Refresh and try again.");
      }
    } catch {
      setError("Couldn't download that file from storage. Refresh and try again.");
    }
  }

  // The conversation guide is EXACTLY the file the content team uploaded to the step's `deck` slot — a
  // short-lived signed GET, filename-attributed (the backend guarantees the right extension for the
  // stored content type). There is no generated/rebuilt fallback: the button is rendered only when a
  // real deck asset is uploaded, and a download failure is surfaced rather than handing over a
  // placeholder.
  async function downloadPitchDeck() {
    if (!(deckIsManaged && deckSource.assetId != null)) return;
    setDeckBusy(true);
    setError(null);
    try {
      const { url } = await getCampaignAssetDownload(
        deckSource.assetId,
        deckLabel || 'Frontier AI Conversation Guide',
      );
      if (url) {
        triggerDownload(url, deckLabel || 'Frontier-AI-Conversation-Guide');
      } else {
        setError("Couldn't download the conversation guide from storage. Refresh and try again.");
      }
    } catch {
      setError("Couldn't download the conversation guide from storage. Refresh and try again.");
    } finally {
      setDeckBusy(false);
    }
  }

  return (
    <Block
      icon={asIconName(config?.icon, 'deck')}
      title={(config?.guideTitle as string) || (config?.title as string) || 'Your conversation guide'}
      lead={
        (config?.guideBody as string) ||
        (config?.lead as string) ||
        'This section has the conversation guide to help you lead the discussion — it is your reference, not something to hand over as-is.'
      }
    >
      {/* All resource buttons share one variant (accent) and stretch to equal width, so no download is
          visually privileged over the others and the row fills the available horizontal space evenly. */}
      <div className={s.exportRow} style={{ flexWrap: 'nowrap' }}>
        {/* Only shown when the content team has uploaded a deck to this step: the conversation guide
            is that file and nothing else — there is no generated placeholder to hand over. */}
        {deckIsManaged && (
          <button
            type="button"
            className={cx(ui.btn, ui.primary)}
            style={{ flex: '1 1 0' }}
            onClick={() => void downloadPitchDeck()}
            disabled={deckBusy}
          >
            {deckBusy ? 'Preparing...' : `${deckLabel || 'Conversation guide'} (${deckFormat})`}
          </button>
        )}
        {/* Shown only when the content team has uploaded a file to the step's slot — no bundled
            fallback is shipped, so an un-referenced/un-uploaded slot simply has no button. */}
        {emailAsset && (
          <button
            type="button"
            className={cx(ui.btn, ui.primary)}
            style={{ flex: '1 1 0' }}
            onClick={() =>
              void downloadManagedAsset(
                emailAsset,
                emailLabel || 'Frontier-AI-Workshop-Email-Template.docx',
              )
            }
          >
            {emailLabel || 'Email template (DOCX)'}
          </button>
        )}
        {onePagerAsset && (
          <button
            type="button"
            className={cx(ui.btn, ui.primary)}
            style={{ flex: '1 1 0' }}
            onClick={() =>
              void downloadManagedAsset(
                onePagerAsset,
                onePagerLabel || 'Frontier-AI-Workshop-One-Pager.pdf',
              )
            }
          >
            {onePagerLabel || 'One pager (PDF)'}
          </button>
        )}
        {onePagerSecurityAsset && (
          <button
            type="button"
            className={cx(ui.btn, ui.primary)}
            style={{ flex: '1 1 0' }}
            onClick={() =>
              void downloadManagedAsset(
                onePagerSecurityAsset,
                onePagerSecurityLabel || 'Frontier-AI-Workshop-One-Pager-Security.pdf',
              )
            }
          >
            {onePagerSecurityLabel || 'One pager (Security)'}
          </button>
        )}
      </div>
    </Block>
  );
}

/**
 * The recommended discovery-questions list asked on the champion call: the seeded corpus (IQ-gated by
 * the scenario Profiling recorded), the Cisco IQ scenario chips, any questions the SE added, and the
 * add-your-own row. Answers commit to `workshopStore` on blur, keyed by `recommendedKey`/per-step ai key.
 */
export function DiscoveryQuestions({ ctx, config }: PrimitiveProps): JSX.Element {
  const { state, stepIndex } = ctx;
  const resolve = useResolveText();
  const setDiscoveryAnswer = useWorkshopStore((st) => st.setDiscoveryAnswer);
  const addAiQuestions = useWorkshopStore((st) => st.addAiQuestions);
  const removeAiQuestion = useWorkshopStore((st) => st.removeAiQuestion);
  const [ownQuestion, setOwnQuestion] = useState('');
  const h = sectionHeadings('propose');

  // Which predefined question blocks this step shows is authored on the step itself (its question
  // refs, edited inline in Admin); fall back to the whole `recommended_question` corpus, then to the
  // bundled set, so an un-seeded / standalone campaign renders exactly as before.
  const refQuestions = resolveQuestionsForStep('propose', 'recommended_question').map(
    (b) => b.payload as ReturnType<typeof resolveRecommendedQuestions>[number],
  );
  const recommendedQuestions = refQuestions.length ? refQuestions : resolveRecommendedQuestions();
  const iqScenarios = resolveIqScenarios();

  // Cisco IQ entitlement was answered in Profiling, so only what it left open
  // is asked on the call.
  const iqConnected = state.connect.iq.connected;
  const iqDeclined = !!state.connect.iq.declined;

  // What the account's own Cisco Ready export already says about unsupported
  // equipment. Quoted on the call, so the question lands as "what will you do"
  // rather than "do you have a problem". (Derivation kernel: `derive-ldos-facts`.)
  const ldosFacts = useMemo(() => deriveLdosFacts(state.installBase), [state.installBase]);

  // Questions the SE added themselves. Positional: the answers are keyed by
  // index, so these are appended and removed by value, never reordered.
  const custom = state.aiQuestions[String(stepIndex)] ?? NO_QUESTIONS;

  // Profiling already established whether the account is entitled to Cisco IQ,
  // so only what it left open gets asked here. Nothing left open means no
  // question at all, rather than asking the champion to confirm a fact the tool
  // has already checked.
  const iqOptions = useMemo(
    () => resolveScenariosFor({ connected: iqConnected, declined: iqDeclined }),
    [iqConnected, iqDeclined],
  );
  // One option left means Profiling already answered it: the chip is shown
  // chosen and locked rather than asked again.
  const iqSettled = iqOptions.length === 1;
  const iqScenario = state.discoveryAnswers[IQ_SCENARIO_KEY] ?? '';

  // The IQ question/rationale text: read from the first scenario in iqOptions (the scenario that
  // corresponds to the current connection state) when the block carries question/why fields, else
  // fall back to the hard-coded strings.
  const firstScenarioId = iqOptions[0];
  const iqScenarioBlocks = resolveContentBlocks('iq_scenario');
  const firstScenarioBlock = iqScenarioBlocks.find((b) => b.key === firstScenarioId);
  const authoredQuestion = firstScenarioBlock?.payload?.question;
  const authoredWhy = firstScenarioBlock?.payload?.why;

  const iqQuestion =
    typeof authoredQuestion === 'string' && authoredQuestion.trim()
      ? authoredQuestion.trim()
      : iqDeclined
        ? 'Cisco IQ is not enabled for this account'
        : iqConnected
          ? 'Cisco IQ is enabled. Is your team actively using it?'
          : 'Is Cisco IQ enabled for this account, and is your team actively using it?';
  const iqWhy =
    typeof authoredWhy === 'string' && authoredWhy.trim()
      ? authoredWhy.trim()
      : iqDeclined
        ? 'Nothing to ask. The deep-dive will run the demo-led scenario.'
        : 'This one decides how the deep-dive is run on the day, so it is the answer to leave the call with.';

  // The deep-dive loads its questions from the recorded scenario, so that
  // scenario has to exist before the SE gets there. When Profiling has left only
  // one possible answer it is written here rather than waiting for a click the
  // SE has no alternative to make; and when Profiling later contradicts a
  // scenario already recorded, the stale answer is dropped rather than loading a
  // deep-dive the entitlement no longer supports.
  useEffect(() => {
    // Decision kernel: `reconcile-iq-scenario` — what (if anything) to write; the effect does the write.
    const next = reconcileIqScenario({ iqSettled, iqOptions, iqScenario });
    if (next) setDiscoveryAnswer(IQ_SCENARIO_KEY, next.write);
  }, [iqSettled, iqOptions, iqScenario, setDiscoveryAnswer]);

  function addOwnQuestion() {
    const text = ownQuestion.trim();
    if (!text) return;
    addAiQuestions(stepIndex, [text]);
    setOwnQuestion('');
  }

  return (
    <Block
      icon={asIconName(config?.icon, 'chat')}
      title={(config?.title as string) || h.discovery?.title || ''}
      lead={(config?.lead as string) || h.discovery?.lead}
    >
      <div className={s.qaList}>
        {(() => {
          // A recommended question may carry a `when` naming one Cisco IQ scenario — the seeded
          // 3-way corpus uses the IqScenario ids ('active' | 'enabled-unused' | 'not-enabled').
          // Show a question only when it is ungated or its gate matches the scenario Profiling
          // recorded; ungated questions always show. (The scenario, not the raw connected flag,
          // is what the seeded content keys off, so entitlement-plus-usage is gated correctly.)
          const visible = recommendedQuestions.filter(
            (item) => !item.when || item.when === iqScenario,
          );
          let lastCategory = '';
          return visible.map((item, idx) => {
            const key = recommendedKey(item.id);
            const id = `rec-${item.id}`;
            const isIq = item.id === 'iq-usage';
            const showCategory = item.category !== lastCategory;
            lastCategory = item.category;
            return (
              <Fragment key={item.id}>
                {showCategory && <p className={s.qaCategory}>{item.category}</p>}
                <div className={s.qaItem}>
                  <label className={s.qaQuestion} htmlFor={id}>
                    <span className={s.qaNum}>{String(idx + 1).padStart(2, '0')}</span>
                    {isIq ? resolve(iqQuestion) : resolve(item.question)}
                    <span className={s.qaGroup}>
                      {isIq && iqSettled ? 'From Profiling' : 'Recommended'}
                    </span>
                  </label>
                  <p className={s.qaWhy}>{isIq ? resolve(iqWhy) : resolve(item.why)}</p>
                  {/* Cisco Ready already has the LDoS figures for this account, so
                      they are put on screen next to the Lifecycle question rather
                      than left for the SE to look up separately. */}
                  {item.id === 'lifecycle-eol' && ldosFacts && (
                    <p className={s.settledNote}>{ldosFacts}</p>
                  )}
                  {/* Profiling already checked the entitlement in Cisco IQ, so
                      only what it left open is asked here. When it left nothing
                      open there is no question, just the answer. */}
                  {isIq && (
                    <>
                      <div className={s.scenarioRow} role="group" aria-label="Cisco IQ status">
                        {iqOptions.map((option) => {
                          // A DB-backed iq_scenario payload can carry ids that don't match the
                          // option ids Profiling produced. Rather than silently drop the chip
                          // (which makes an answer un-selectable), fall back to a minimal scenario
                          // derived from the option id so every option still renders.
                          const scenario = iqScenarios.find((sc) => sc.id === option) ?? {
                            id: option,
                            label: option.replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
                            detail: '',
                          };
                          const on = iqSettled || iqScenario === scenario.id;
                          return (
                            <button
                              key={scenario.id}
                              type="button"
                              aria-pressed={on}
                              disabled={iqSettled}
                              className={cx(
                                s.scenarioChip,
                                on && s.scenarioChipOn,
                                iqSettled && s.scenarioChipLocked,
                              )}
                              onClick={() =>
                                setDiscoveryAnswer(IQ_SCENARIO_KEY, on ? '' : scenario.id)
                              }
                            >
                              <b>{scenario.label}</b>
                              <span>{scenario.detail}</span>
                            </button>
                          );
                        })}
                      </div>
                      {iqSettled && <p className={s.settledNote}>Recorded in Profiling.</p>}
                    </>
                  )}
                  {/* Nothing to write down when the answer is already settled. */}
                  {(!isIq || !iqSettled) && (
                    <BlurTextarea
                      id={id}
                      className={ui.textarea}
                      placeholder="What the customer said..."
                      value={state.discoveryAnswers[key] ?? ''}
                      onCommit={(v) => setDiscoveryAnswer(key, v)}
                    />
                  )}
                </div>
              </Fragment>
            );
          });
        })()}

        {/* Anything account-specific the SE wants to ask, alongside the recommended set. */}
        {custom.map((question, idx) => {
          const key = `${stepIndex}-ai${idx + 1}`;
          const id = `own-${idx}`;
          return (
            <div key={question} className={s.qaItem}>
              <label className={s.qaQuestion} htmlFor={id}>
                <span className={s.qaNum}>
                  {String(recommendedQuestions.length + idx + 1).padStart(2, '0')}
                </span>
                {resolve(question)}
                <span className={s.qaGroup}>Yours</span>
              </label>
              <BlurTextarea
                id={id}
                className={ui.textarea}
                placeholder="What the customer said..."
                value={state.discoveryAnswers[key] ?? ''}
                onCommit={(v) => setDiscoveryAnswer(key, v)}
              />
              <button
                type="button"
                className={s.qaRemove}
                onClick={() => removeAiQuestion(stepIndex, question)}
              >
                Remove this question
              </button>
            </div>
          );
        })}
      </div>

      <div className={s.addQuestionRow}>
        <input
          className={s.addQuestionInput}
          placeholder="Add a question of your own..."
          aria-label="Add a question of your own"
          value={ownQuestion}
          onChange={(e) => setOwnQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOwnQuestion();
            }
          }}
        />
        <button
          type="button"
          className={cx(ui.btn, ui.ghost)}
          onClick={addOwnQuestion}
          disabled={!ownQuestion.trim()}
        >
          Add question
        </button>
      </div>
    </Block>
  );
}
